import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dropzone",
    "uploading",
    "processing",
    "error",
    "fileInput",
    "fileName",
    "progressBar",
    "errorMessage"
  ]
  static values = {
    parseUrl: String,
    formUrl: String
  }

  declare readonly dropzoneTarget: HTMLElement
  declare readonly uploadingTarget: HTMLElement
  declare readonly processingTarget: HTMLElement
  declare readonly errorTarget: HTMLElement
  declare readonly fileInputTarget: HTMLInputElement
  declare readonly fileNameTarget: HTMLElement
  declare readonly progressBarTarget: HTMLElement
  declare readonly errorMessageTarget: HTMLElement
  declare readonly parseUrlValue: string
  declare readonly formUrlValue: string

  private selectedFile: File | null = null

  openFilePicker() {
    this.fileInputTarget.click()
  }

  fileSelected(event: Event) {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0])
    }
  }

  dragOver(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  dragEnter(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    const dropzone = this.dropzoneTarget.querySelector(".border-dashed")
    if (dropzone) {
      dropzone.classList.add("border-purple-500", "bg-purple-50")
      dropzone.classList.remove("border-gray-300")
    }
  }

  dragLeave(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    const dropzone = this.dropzoneTarget.querySelector(".border-dashed")
    if (dropzone) {
      dropzone.classList.remove("border-purple-500", "bg-purple-50")
      dropzone.classList.add("border-gray-300")
    }
  }

  drop(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()

    const dropzone = this.dropzoneTarget.querySelector(".border-dashed")
    if (dropzone) {
      dropzone.classList.remove("border-purple-500", "bg-purple-50")
      dropzone.classList.add("border-gray-300")
    }

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0])
    }
  }

  handleFile(file: File) {
    // Validate file type
    const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"]
    if (!allowedTypes.includes(file.type)) {
      this.showError("Please upload a PDF or image file (PNG, JPG)")
      return
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      this.showError("File size must be less than 10MB")
      return
    }

    this.selectedFile = file
    this.uploadFile(file)
  }

  async uploadFile(file: File) {
    this.showUploading(file.name)

    const formData = new FormData()
    formData.append("document", file)

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

    try {
      // Simulate upload progress
      this.animateProgress()

      const response = await fetch(this.parseUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken || "",
          Accept: "application/json"
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error("Upload failed")
      }

      this.showProcessing()

      const result = await response.json()

      if (result.success) {
        // Redirect to show page where user can see parsing progress
        window.location.href = result.redirect_url
      } else {
        this.showError(result.error || "Failed to upload document")
      }
    } catch (error) {
      this.showError("Something went wrong. Please try again.")
    }
  }

  animateProgress() {
    let progress = 0
    const interval = setInterval(() => {
      progress += Math.random() * 15
      if (progress >= 90) {
        progress = 90
        clearInterval(interval)
      }
      this.progressBarTarget.style.width = `${progress}%`
    }, 200)
  }

  showUploading(fileName: string) {
    this.dropzoneTarget.classList.add("hidden")
    this.uploadingTarget.classList.remove("hidden")
    this.processingTarget.classList.add("hidden")
    this.errorTarget.classList.add("hidden")
    this.fileNameTarget.textContent = fileName
    this.progressBarTarget.style.width = "0%"
  }

  showProcessing() {
    this.dropzoneTarget.classList.add("hidden")
    this.uploadingTarget.classList.add("hidden")
    this.processingTarget.classList.remove("hidden")
    this.errorTarget.classList.add("hidden")
  }

  showError(message: string) {
    this.dropzoneTarget.classList.add("hidden")
    this.uploadingTarget.classList.add("hidden")
    this.processingTarget.classList.add("hidden")
    this.errorTarget.classList.remove("hidden")
    this.errorMessageTarget.textContent = message
  }

  retry() {
    this.dropzoneTarget.classList.remove("hidden")
    this.uploadingTarget.classList.add("hidden")
    this.processingTarget.classList.add("hidden")
    this.errorTarget.classList.add("hidden")
    this.selectedFile = null
    this.fileInputTarget.value = ""
  }
}
